import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

import { Add } from '@mxenabled/mx-icons'

import { useGlobalCopyStore } from '../../../common'
import { Frequency, type Transaction } from '../../../core'
import SubheaderRow from '../../../common/components/SubheaderRow'
import { OnboardingSteps } from '../../../core/types/Finstrong'
import { fromUnixToDayOfWeekAndDate } from '../../../core/utils/DateUtil'
import { AddRecurringTransactionDetails } from '../../../recurringtransactions'
import { TransactionRow } from '../../../transactions'
import { hasInsetBorder, shouldShowDate } from '../../../transactions/utils/Transactions'

import type { StepState } from './OnboardingStepsDrawer'

interface OnboardingAddMonthlyIncomeProps {
  firstDay: number
  frequency: Frequency
  secondDay: number
  incomeTransactions: Transaction[]
  setCurrentStep: (step: StepState) => void
  setFirstDay: (day: number) => void
  setFrequency: (frequency: Frequency) => void
  setSecondDay: (day: number) => void
  transaction: Transaction
}

const OnboardingAddMonthlyIncome = ({
  firstDay,
  frequency,
  secondDay,
  incomeTransactions,
  setCurrentStep,
  setFirstDay,
  setFrequency,
  setSecondDay,
  transaction,
}: OnboardingAddMonthlyIncomeProps) => {
  const { finstrong: copy } = useGlobalCopyStore()

  return (
    <React.Fragment>
      <Stack>
        {!!incomeTransactions.length && (
          <Box sx={{ pb: 24 }}>
            {incomeTransactions.reduce<React.JSX.Element[]>((elements, txn, i) => {
              if (txn.guid === transaction.guid) return elements

              const showDate = shouldShowDate(i, incomeTransactions)
              const isInset = hasInsetBorder(i, incomeTransactions)

              elements.push(
                <React.Fragment key={txn.guid}>
                  {showDate && <SubheaderRow>{fromUnixToDayOfWeekAndDate(txn.date)}</SubheaderRow>}
                  <TransactionRow component="div" transaction={txn} />
                  <Divider sx={{ ml: isInset ? 44 : 0 }} />
                </React.Fragment>,
              )

              return elements
            }, [])}
          </Box>
        )}
        <AddRecurringTransactionDetails
          firstDay={firstDay}
          frequency={frequency}
          onFirstDayChange={setFirstDay}
          onFrequencyChange={setFrequency}
          onSecondDayChange={setSecondDay}
          secondDay={secondDay}
          transaction={transaction}
        />
      </Stack>

      <Box sx={{ px: 24 }}>
        <Button
          fullWidth={true}
          onClick={() => setCurrentStep({ step: OnboardingSteps.MonthlyIncome, type: 'main' })}
          startIcon={<Add />}
        >
          {copy.onboarding_add_more_income}
        </Button>
      </Box>
    </React.Fragment>
  )
}

export default observer(OnboardingAddMonthlyIncome)
